import React, { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import Stack from '@mui/material/Stack';
import DeploymentWeeks from '../../models/organization/deployment-week';
import { getAggregatedData } from '../../utils/organization-stats-handlers';
import WeeklyCleaningPerformanceChart from './stats/WeeklyCleaningPerformanceChart';
import WeeklyCleanedAreaChart from './stats/WeeklyCleanedAreaChart';
import WeeklyCleaningDurationChart from './stats/WeeklyCleaningDurationChart';
import RobotCards from './stats/RobotCards';

const OrganizationData: React.FC<{
  deploymentWeeks: DeploymentWeeks[] | null;
  weekFilter: { start: DateTime | null; end: DateTime | null };
  filteredRobotIds: string[] | null;
  loading: boolean;
}> = ({ deploymentWeeks, weekFilter, filteredRobotIds, loading }) => {
  const filterDeployments = useCallback(
    (deployment: DeploymentWeeks) => {
      return (
        filteredRobotIds == null ||
        filteredRobotIds.length === 0 ||
        filteredRobotIds.includes(deployment.robotId)
      );
    },
    [filteredRobotIds],
  );

  const aggregatedData = useMemo(
    () => getAggregatedData(deploymentWeeks, filterDeployments),
    [deploymentWeeks, filterDeployments],
  );

  const weekFilterHandler = (week: string) => {
    if (weekFilter.start == null || weekFilter.end == null) return true;
    return (
      DateTime.fromISO(week).toMillis() > weekFilter.start.toMillis() &&
      DateTime.fromISO(week).toMillis() < weekFilter.end.toMillis()
    );
  };

  const robotCount = useMemo(
    () => Array.from(new Set(deploymentWeeks?.flatMap((dw) => dw.robotId))).length,
    [deploymentWeeks],
  );
  const locationCount = useMemo(
    () =>
      Array.from(
        new Set(deploymentWeeks?.filter(filterDeployments)?.flatMap((dw) => dw.organizationId)),
      ).length,
    [deploymentWeeks, filterDeployments],
  );

  return (
    <React.Fragment>
      <Stack spacing={2}>
        <RobotCards
          filterApplied={
            (weekFilter.start != null && weekFilter.end != null) || filteredRobotIds != null
          }
          robotCount={
            filteredRobotIds != null && filteredRobotIds.length > 0
              ? filteredRobotIds.length
              : robotCount
          }
          cleanedArea={aggregatedData?.totalCleanedArea ?? 0}
          operatingTime={aggregatedData?.totalOperatingTime ?? 0}
          locationCount={locationCount}
          loading={loading}
        />
        <WeeklyCleaningPerformanceChart
          aggregatedData={aggregatedData}
          filterWeeks={weekFilterHandler}
          loading={loading}
        />
        <WeeklyCleanedAreaChart
          aggregatedData={aggregatedData}
          filterWeeks={weekFilterHandler}
          loading={loading}
        />

        <WeeklyCleaningDurationChart
          aggregatedData={aggregatedData}
          filterWeeks={weekFilterHandler}
          loading={loading}
        />
      </Stack>
    </React.Fragment>
  );
};

export default OrganizationData;
